import { useEffect, useState } from "react";
import { api, fmtNum, fmtIDR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, BarChart, Bar, Legend, AreaChart, Area
} from "recharts";
import {
  Users2, Eye, ShoppingBag, Wallet, TrendingUp, TrendingDown,
  Radio, Sparkles, Activity, Clock, Zap, Globe, MousePointerClick, Timer,
  UserPlus, MapPin, Compass, Monitor, Smartphone, Tablet, ShieldAlert,
  Megaphone, Copy, Check
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const CHART_COLORS = ["#007AFF", "#00F0FF", "#00E676", "#FFCC00", "#FF3B30", "#B180FF", "#FF7A00"];

const KPI = ({ label, value, icon: Icon, tone = "default", delta, testid }) => (
  <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-5 flex flex-col gap-3 hover:bg-[#0D0D0D] transition" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="label-mono">{label}</span>
      <Icon size={16} strokeWidth={1.5} className="text-zinc-500" />
    </div>
    <div className="font-mono text-3xl font-medium tracking-tight">{value}</div>
    {delta !== undefined && (
      <div className={`label-mono flex items-center gap-1 ${delta >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
        {delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        {delta >= 0 ? "+" : ""}{delta}% vs periode lalu
      </div>
    )}
  </div>
);

const Section = ({ title, subtitle, right, children }) => (
  <section className="border border-[#1F1F1F] bg-[#0A0A0A]">
    <div className="flex items-center justify-between px-5 py-4 border-b border-[#1F1F1F]">
      <div>
        <h3 className="font-display font-bold tracking-tight text-base">{title}</h3>
        {subtitle && <p className="label-mono mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const Ga4LiveSection = ({ ga4, loading, error, onRefresh, excludeBots, onToggleBots }) => {
  const isDown = !loading && (error || !ga4);
  return (
    <section className="border border-[#1F1F1F] bg-[#0A0A0A]" data-testid="ga4-live-section">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 border-b border-[#1F1F1F]">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Globe size={16} className="text-[#00F0FF]" />
            {ga4?.active_users_today > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#00E676] rounded-full animate-pulse" />
            )}
          </div>
          <div>
            <h3 className="font-display font-bold tracking-tight text-base flex items-center gap-2">
              Google Analytics 4 <span className="label-mono text-[#00E676]">// live</span>
            </h3>
            <p className="label-mono mt-0.5">
              {ga4?.property_id ? `property_id · ${ga4.property_id}` : "menghubungkan..."}
              {ga4 && ` · ${ga4.range_days}d window`}
              {ga4?.exclude_bots && ga4?.excluded_bot_channels?.length > 0 && (
                <span className="text-[#00E676] ml-1">
                  · filter aktif: excluded {ga4.excluded_bot_channels.join(", ")}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label
            className="flex items-center gap-2 cursor-pointer border border-[#1F1F1F] hover:border-[#00E676] px-3 py-1.5 label-mono transition"
            data-testid="ga4-exclude-bots-toggle"
            title="Sembunyikan sessions dari channel groups dengan engagement <10% & Unassigned"
          >
            <input
              type="checkbox"
              checked={excludeBots}
              onChange={(e) => onToggleBots(e.target.checked)}
              className="accent-[#00E676] w-3.5 h-3.5"
              data-testid="ga4-exclude-bots-checkbox"
            />
            <ShieldAlert size={12} className={excludeBots ? "text-[#00E676]" : "text-zinc-500"} />
            <span className={excludeBots ? "text-[#00E676]" : "text-zinc-400"}>
              filter bot
            </span>
          </label>
          <button
            onClick={onRefresh}
            disabled={loading}
            data-testid="ga4-refresh-btn"
            className="border border-[#1F1F1F] hover:border-[#00F0FF] px-3 py-1.5 label-mono flex items-center gap-2 transition disabled:opacity-50"
          >
            <Zap size={12} className={loading ? "animate-pulse" : ""} />
            {loading ? "loading" : "refresh"}
          </button>
        </div>
      </div>

      {isDown && (
        <div className="p-8 text-center label-mono text-[#FF3B30]" data-testid="ga4-error">
          // ga4 unavailable · {error || "kredensial belum dikonfigurasi"}
        </div>
      )}

      {!isDown && (
        <div className="p-5 space-y-5">
          {/* Live KPI cards - row 1 (Realtime + core traffic) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-active-today">
              <div className="flex items-center justify-between">
                <span className="label-mono">pengunjung hari ini</span>
                <Users2 size={14} className="text-[#00E676]" />
              </div>
              <div className="font-mono text-3xl mt-2 text-[#00E676]">
                {loading && !ga4 ? "..." : fmtNum(ga4?.active_users_today || 0)}
              </div>
              <div className="label-mono mt-1 text-zinc-600">// realtime (30m window)</div>
            </div>

            <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-sessions">
              <div className="flex items-center justify-between">
                <span className="label-mono">sessions</span>
                <MousePointerClick size={14} className="text-[#00F0FF]" />
              </div>
              <div className="font-mono text-3xl mt-2">
                {loading && !ga4 ? "..." : fmtNum(ga4?.sessions || 0)}
              </div>
              <div className="label-mono mt-1 text-zinc-600">// {ga4?.range_days || 30} hari</div>
            </div>

            <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-pageviews">
              <div className="flex items-center justify-between">
                <span className="label-mono">page views</span>
                <Eye size={14} className="text-[#B180FF]" />
              </div>
              <div className="font-mono text-3xl mt-2">
                {loading && !ga4 ? "..." : fmtNum(ga4?.page_views || 0)}
              </div>
              <div className="label-mono mt-1 text-zinc-600">// total tayangan halaman</div>
            </div>

            <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-session-duration">
              <div className="flex items-center justify-between">
                <span className="label-mono">avg session</span>
                <Timer size={14} className="text-[#FFCC00]" />
              </div>
              <div className="font-mono text-3xl mt-2">
                {loading && !ga4 ? "..." : `${Math.round(ga4?.avg_session_duration || 0)}s`}
              </div>
              <div className="label-mono mt-1 text-zinc-600">// bounce {ga4?.bounce_rate || 0}%</div>
            </div>
          </div>

          {/* Live KPI cards - row 2 (Users deep-dive) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-total-users">
              <div className="flex items-center justify-between">
                <span className="label-mono">total users</span>
                <Users2 size={14} className="text-[#007AFF]" />
              </div>
              <div className="font-mono text-3xl mt-2">
                {loading && !ga4 ? "..." : fmtNum(ga4?.total_users || 0)}
              </div>
              <div className="label-mono mt-1 text-zinc-600">// pengunjung unik · {ga4?.range_days || 30}d</div>
            </div>

            <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-new-users">
              <div className="flex items-center justify-between">
                <span className="label-mono">new users</span>
                <UserPlus size={14} className="text-[#00E676]" />
              </div>
              <div className="font-mono text-3xl mt-2">
                {loading && !ga4 ? "..." : fmtNum(ga4?.new_users || 0)}
              </div>
              <div className="label-mono mt-1 text-zinc-600">
                {ga4?.total_users
                  ? `// ${Math.round((ga4.new_users / ga4.total_users) * 100)}% dari total users`
                  : "// pengunjung pertama kali"}
              </div>
            </div>

            <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-avg-engagement">
              <div className="flex items-center justify-between">
                <span className="label-mono">avg engagement time</span>
                <Activity size={14} className="text-[#FF7A00]" />
              </div>
              <div className="font-mono text-3xl mt-2">
                {loading && !ga4 ? "..." : `${Math.round(ga4?.avg_engagement_time || 0)}s`}
              </div>
              <div className="label-mono mt-1 text-zinc-600">// per active user</div>
            </div>
          </div>

          {/* Trend chart + Top Pages */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border border-[#1F1F1F] bg-[#050505] p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="label-mono text-zinc-500">// tren kunjungan harian (real-time)</div>
                  <div className="font-display font-bold text-sm mt-0.5">Active Users & Sessions · 30 hari</div>
                </div>
                <span className="label-mono text-[#00F0FF]">{ga4?.daily_trend?.length || 0} data points</span>
              </div>
              {ga4?.daily_trend?.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={ga4.daily_trend} data-testid="ga4-trend-chart">
                    <defs>
                      <linearGradient id="ga4-users" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00E676" stopOpacity={0.5}/>
                        <stop offset="100%" stopColor="#00E676" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="ga4-sess" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.4}/>
                        <stop offset="100%" stopColor="#00F0FF" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 4" stroke="#1F1F1F" />
                    <XAxis dataKey="date" stroke="#71717A" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#71717A" fontSize={10} tickFormatter={fmtNum} />
                    <Tooltip contentStyle={{ background: "#050505", border: "1px solid #1F1F1F", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono", color: "#A1A1AA" }} />
                    <Area type="monotone" dataKey="active_users" name="Active Users" stroke="#00E676" strokeWidth={2} fill="url(#ga4-users)" />
                    <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#00F0FF" strokeWidth={2} fill="url(#ga4-sess)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-zinc-500 text-sm py-16 text-center label-mono">
                  {loading ? "memuat data ga4..." : "tidak ada data tren"}
                </div>
              )}
            </div>

            <div className="border border-[#1F1F1F] bg-[#050505] p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="label-mono text-zinc-500">// top pages</div>
                  <div className="font-display font-bold text-sm mt-0.5">Halaman Terpopuler</div>
                </div>
                <span className="label-mono text-[#B180FF]">top {ga4?.top_pages?.length || 0}</span>
              </div>
              <div className="space-y-2 max-h-[260px] overflow-y-auto" data-testid="ga4-top-pages">
                {ga4?.top_pages?.length ? ga4.top_pages.map((p, i) => (
                  <div key={p.path + i} className="border-l-2 border-[#1F1F1F] hover:border-[#00F0FF] pl-3 py-1 transition"
                       data-testid={`ga4-top-page-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-600 w-5">{String(i+1).padStart(2,"0")}</span>
                      <span className="font-mono text-xs text-[#00F0FF] truncate flex-1" title={p.path}>{p.path}</span>
                      <span className="font-mono text-xs text-white">{fmtNum(p.views)}</span>
                    </div>
                    <div className="text-xs text-zinc-500 truncate pl-7 mt-0.5" title={p.title}>{p.title || "-"}</div>
                  </div>
                )) : (
                  <div className="text-zinc-500 text-sm py-8 text-center label-mono">
                    {loading ? "loading..." : "tidak ada data"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Cities panel */}
          <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-top-cities-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-[#FFCC00]" />
                <div>
                  <div className="label-mono text-zinc-500">// top cities</div>
                  <div className="font-display font-bold text-sm mt-0.5">Kota Asal Pengunjung Terbanyak</div>
                </div>
              </div>
              <span className="label-mono text-[#FFCC00]">top {ga4?.top_cities?.length || 0}</span>
            </div>
            {ga4?.top_cities?.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ga4-top-cities">
                {ga4.top_cities.map((c, i) => {
                  const maxUsers = ga4.top_cities[0]?.active_users || 1;
                  const pct = Math.round((c.active_users / maxUsers) * 100);
                  return (
                    <div key={c.city + i} className="border border-[#1F1F1F] p-3 hover:border-[#FFCC00] transition"
                         data-testid={`ga4-city-${i}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-mono text-xs text-zinc-600">#{i + 1}</span>
                        <span className="label-mono text-zinc-500 truncate" title={c.country}>{c.country || "-"}</span>
                      </div>
                      <div className="font-display font-bold text-base mt-1 truncate" title={c.city}>{c.city}</div>
                      <div className="font-mono text-xl text-[#FFCC00] mt-1">{fmtNum(c.active_users)}</div>
                      <div className="label-mono text-zinc-600 mt-0.5">// {fmtNum(c.sessions)} sessions</div>
                      <div className="h-1 bg-[#1F1F1F] mt-2 relative">
                        <div className="h-full bg-gradient-to-r from-[#FFCC00] to-[#FF7A00]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-zinc-500 text-sm py-8 text-center label-mono">
                {loading ? "loading..." : "tidak ada data kota"}
              </div>
            )}
          </div>

          {/* Traffic Sources + Devices */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Traffic Sources */}
            <div className="lg:col-span-2 border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-traffic-sources-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Compass size={14} className="text-[#00F0FF]" />
                  <div>
                    <div className="label-mono text-zinc-500">// traffic sources</div>
                    <div className="font-display font-bold text-sm mt-0.5">Sumber Traffic · Channel Groups</div>
                  </div>
                </div>
                <div className="label-mono text-zinc-500 flex items-center gap-1.5">
                  <ShieldAlert size={11} className="text-[#FF3B30]" />
                  &lt;10% engagement = suspected bot
                </div>
              </div>
              {ga4?.traffic_sources?.length ? (
                <div className="space-y-2" data-testid="ga4-traffic-sources">
                  {ga4.traffic_sources.map((s, i) => {
                    const maxSess = ga4.traffic_sources[0]?.sessions || 1;
                    const pct = Math.round((s.sessions / maxSess) * 100);
                    const isBot = s.engagement_rate < 10 && s.sessions > 20;
                    const engColor = s.engagement_rate >= 50 ? "text-[#00E676]"
                      : s.engagement_rate >= 20 ? "text-[#FFCC00]"
                      : "text-[#FF3B30]";
                    return (
                      <div key={s.channel + i} className="border-l-2 border-[#1F1F1F] hover:border-[#00F0FF] pl-3 py-2 transition"
                           data-testid={`ga4-source-${i}`}>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs text-zinc-600 w-5">{String(i+1).padStart(2,"0")}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-display font-bold text-sm truncate">{s.channel}</span>
                              {isBot && (
                                <span className="label-mono text-[#FF3B30] border border-[#FF3B30] px-1.5 py-0.5" title="Engagement rate rendah — kemungkinan bot traffic">
                                  bot?
                                </span>
                              )}
                            </div>
                            <div className="h-1 bg-[#1F1F1F] mt-1.5 relative">
                              <div className="h-full bg-gradient-to-r from-[#007AFF] to-[#00F0FF]" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                          <div className="text-right w-20">
                            <div className="font-mono text-sm text-white">{fmtNum(s.sessions)}</div>
                            <div className="label-mono text-zinc-600">sessions</div>
                          </div>
                          <div className="text-right w-20 hidden sm:block">
                            <div className={`font-mono text-sm ${engColor}`}>{s.engagement_rate}%</div>
                            <div className="label-mono text-zinc-600">engaged</div>
                          </div>
                          <div className="text-right w-20 hidden lg:block">
                            <div className="font-mono text-sm text-zinc-300">{Math.round(s.avg_session_duration)}s</div>
                            <div className="label-mono text-zinc-600">avg</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-zinc-500 text-sm py-8 text-center label-mono">
                  {loading ? "loading..." : "tidak ada data sumber"}
                </div>
              )}
            </div>

            {/* Devices */}
            <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="ga4-devices-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Monitor size={14} className="text-[#B180FF]" />
                  <div>
                    <div className="label-mono text-zinc-500">// devices</div>
                    <div className="font-display font-bold text-sm mt-0.5">Perangkat Pengunjung</div>
                  </div>
                </div>
              </div>
              {ga4?.devices?.length ? (() => {
                const total = ga4.devices.reduce((a, b) => a + b.sessions, 0);
                const iconMap = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
                const colorMap = { desktop: "#B180FF", mobile: "#00F0FF", tablet: "#FFCC00" };
                return (
                  <div className="space-y-3" data-testid="ga4-devices">
                    {ga4.devices.map((d, i) => {
                      const Icon = iconMap[d.device] || Monitor;
                      const color = colorMap[d.device] || "#71717A";
                      const pct = total ? Math.round((d.sessions / total) * 100) : 0;
                      return (
                        <div key={d.device + i} data-testid={`ga4-device-${d.device}`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <Icon size={14} style={{ color }} />
                            <span className="font-display font-bold text-sm capitalize flex-1">{d.device}</span>
                            <span className="font-mono text-sm text-white">{pct}%</span>
                          </div>
                          <div className="h-2 bg-[#1F1F1F] relative">
                            <div className="h-full" style={{ background: color, width: `${pct}%` }} />
                          </div>
                          <div className="label-mono text-zinc-600 mt-1">
                            // {fmtNum(d.sessions)} sessions · {fmtNum(d.active_users)} users
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })() : (
                <div className="text-zinc-500 text-sm py-8 text-center label-mono">
                  {loading ? "loading..." : "tidak ada data perangkat"}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const AiContentRecommendation = ({ disabled }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const generate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/ai/content-recommendations", { days });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal generate rekomendasi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border border-[#1F1F1F] bg-[#0A0A0A]" data-testid="ai-content-rec-section">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 border-b border-[#1F1F1F]">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Sparkles size={16} className="text-[#B180FF]" />
          </div>
          <div>
            <h3 className="font-display font-bold tracking-tight text-base flex items-center gap-2">
              AI Content Recommendation <span className="label-mono text-[#B180FF]">// gpt-5.2 + ga4 real data</span>
            </h3>
            <p className="label-mono mt-0.5">
              rekomendasi topik, jam posting, dan format konten berbasis data user real (bot sudah difilter)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-[#1F1F1F]" data-testid="ai-rec-days-tabs">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                disabled={loading}
                data-testid={`ai-rec-days-${d}`}
                className={`label-mono px-3 py-1.5 ${days === d ? "bg-[#B180FF] text-white" : "text-zinc-500 hover:text-white"}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            onClick={generate}
            disabled={loading || disabled}
            data-testid="ai-rec-generate-btn"
            className="border border-[#B180FF] bg-[#B180FF]/10 hover:bg-[#B180FF]/20 px-4 py-1.5 label-mono flex items-center gap-2 transition disabled:opacity-40"
          >
            <Sparkles size={12} className={loading ? "animate-pulse text-[#B180FF]" : "text-[#B180FF]"} />
            {loading ? "generating..." : result ? "regenerate" : "generate insights"}
          </button>
        </div>
      </div>

      <div className="p-5">
        {error && (
          <div className="label-mono text-[#FF3B30] py-4" data-testid="ai-rec-error">
            // {error}
          </div>
        )}

        {!result && !loading && !error && (
          <div className="text-zinc-500 text-sm label-mono flex items-center gap-2 py-8">
            <Sparkles size={14} className="text-[#B180FF]" />
            klik <span className="text-white">generate insights</span> untuk analisis konten dari data ga4 real Anda ({days} hari terakhir)
          </div>
        )}

        {loading && (
          <div className="label-mono text-zinc-500 py-8 flex items-center gap-2">
            <Clock size={14} className="animate-spin text-[#B180FF]" />
            gpt-5.2 sedang menganalisis {days} hari data ga4... (~10-20 detik)
          </div>
        )}

        {result && (
          <div data-testid="ai-rec-result">
            {result.peak_hours_wib?.length > 0 && (
              <div className="mb-4 p-3 border border-[#1F1F1F] bg-[#050505]">
                <div className="label-mono text-zinc-500 mb-2">// peak hours (property timezone)</div>
                <div className="flex flex-wrap gap-2">
                  {result.peak_hours_wib.map((h, i) => (
                    <div key={i} className="border border-[#B180FF]/40 px-3 py-1 font-mono text-xs">
                      <span className="text-[#B180FF]">{String(h.hour).padStart(2, "0")}:00</span>
                      <span className="text-zinc-500 ml-2">{fmtNum(h.active_users)} users</span>
                    </div>
                  ))}
                  {result.excluded_bot_channels?.length > 0 && (
                    <div className="border border-[#00E676]/40 px-3 py-1 font-mono text-xs text-[#00E676]">
                      bot filter: {result.excluded_bot_channels.join(", ")}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="prose prose-invert prose-sm max-w-none font-body text-zinc-300 whitespace-pre-wrap">
              {result.recommendations}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};


const PeakHoursBreakdown = ({ disabled }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/dashboard/ga4/peak-hours", {
          params: { days: 30, top_cities: 5, exclude_bots: true },
        });
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || "Gagal memuat peak hours");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (!disabled) load();
    return () => { cancelled = true; };
  }, [disabled]);

  const renderHeatmapRow = (label, values, color, total, subtitle) => {
    const max = Math.max(...values, 1);
    return (
      <div className="flex items-center gap-3 py-1.5" data-testid={`peak-row-${label}`}>
        <div className="w-24 shrink-0">
          <div className="font-display font-bold text-sm truncate" title={label}>{label}</div>
          <div className="label-mono text-zinc-600 truncate">{subtitle} · {fmtNum(total)}</div>
        </div>
        <div className="flex-1 grid grid-cols-24 gap-0.5" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
          {values.map((v, h) => {
            const intensity = v / max;
            const bg = intensity > 0
              ? `rgba(${color}, ${Math.max(0.15, intensity)})`
              : "#0F0F0F";
            return (
              <div
                key={h}
                className="h-8 border border-[#1F1F1F] hover:border-white cursor-help transition"
                style={{ background: bg }}
                title={`${String(h).padStart(2, "0")}:00 WIB — ${fmtNum(v)} active users`}
                data-testid={`peak-cell-${label}-${h}`}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section className="border border-[#1F1F1F] bg-[#0A0A0A]" data-testid="peak-hours-section">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 border-b border-[#1F1F1F]">
        <div className="flex items-center gap-3">
          <Clock size={16} className="text-[#00E676]" />
          <div>
            <h3 className="font-display font-bold tracking-tight text-base flex items-center gap-2">
              Peak Hours Breakdown <span className="label-mono text-[#00E676]">// device + city × 24h</span>
            </h3>
            <p className="label-mono mt-0.5">
              heatmap 24 jam per device dan per kota — gunakan untuk targeted ads scheduling
            </p>
          </div>
        </div>
        {data?.excluded_bot_channels?.length > 0 && (
          <span className="label-mono text-[#00E676] border border-[#00E676]/40 px-2 py-1">
            bot filter: {data.excluded_bot_channels.join(", ")}
          </span>
        )}
      </div>

      <div className="p-5">
        {loading && (
          <div className="label-mono text-zinc-500 py-8 flex items-center gap-2">
            <Clock size={14} className="animate-spin text-[#00E676]" />
            memuat breakdown ga4...
          </div>
        )}
        {error && !loading && (
          <div className="label-mono text-[#FF3B30] py-4">// {error}</div>
        )}
        {data && !loading && (
          <div className="space-y-6">
            {/* Hour axis */}
            <div className="flex items-center gap-3 label-mono text-zinc-600">
              <div className="w-24 shrink-0">// hour (0-23)</div>
              <div className="flex-1 grid" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="text-center text-[10px]">{h % 3 === 0 ? String(h).padStart(2, "0") : ""}</div>
                ))}
              </div>
            </div>

            {/* By Device */}
            <div data-testid="peak-by-device">
              <div className="label-mono text-zinc-500 mb-2">// by device</div>
              {["mobile", "desktop", "tablet"].map((dev) => {
                if (!data.by_device[dev]) return null;
                const color = dev === "mobile" ? "0, 240, 255"
                  : dev === "desktop" ? "177, 128, 255"
                  : "255, 204, 0";
                return renderHeatmapRow(dev, data.by_device[dev], color, data.device_totals[dev] || 0, "active users");
              })}
            </div>

            {/* By City */}
            <div data-testid="peak-by-city">
              <div className="label-mono text-zinc-500 mb-2">// by top 5 cities</div>
              {Object.keys(data.by_city).map((city, i) => {
                const colors = ["255, 204, 0", "255, 122, 0", "0, 230, 118", "0, 122, 255", "177, 128, 255"];
                return renderHeatmapRow(city, data.by_city[city], colors[i % colors.length], data.city_totals[city] || 0, "active users");
              })}
            </div>

            {/* Insight footer */}
            <div className="border-t border-[#1F1F1F] pt-4 mt-2 label-mono text-zinc-500">
              <span className="text-white">tip:</span> cari sel dengan warna paling terang untuk kombinasi device + jam terbaik.
              hover cell untuk detail. mobile Jakarta jam 07:00 = target ad IG Reels; desktop jam 03-04:00 = target SEO publish.
            </div>
          </div>
        )}
      </div>
    </section>
  );
};


const PLATFORMS = [
  { id: "all", label: "Multi-platform" },
  { id: "meta_ads", label: "Meta Ads" },
  { id: "tiktok_ads", label: "TikTok Ads" },
  { id: "google_ads", label: "Google Ads" },
];

const AdsCampaignBrief = ({ disabled }) => {
  const [platform, setPlatform] = useState("meta_ads");
  const [goal, setGoal] = useState("");
  const [budget, setBudget] = useState(150000);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/ai/ads-campaign-brief", {
        platform,
        goal,
        budget_idr: budget || null,
        days,
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal generate brief");
    } finally {
      setLoading(false);
    }
  };

  const copyBrief = async () => {
    if (!result?.brief) return;
    try {
      await navigator.clipboard.writeText(result.brief);
      setCopied(true);
      toast.success("Brief tersalin ke clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy gagal");
    }
  };

  return (
    <section className="border border-[#1F1F1F] bg-[#0A0A0A]" data-testid="ads-brief-section">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 border-b border-[#1F1F1F]">
        <div className="flex items-center gap-3">
          <Megaphone size={16} className="text-[#FF7A00]" />
          <div>
            <h3 className="font-display font-bold tracking-tight text-base flex items-center gap-2">
              Ads Campaign Brief Generator <span className="label-mono text-[#FF7A00]">// gpt-5.2 + ga4 real</span>
            </h3>
            <p className="label-mono mt-0.5">
              brief campaign siap copy-paste ke Ads Manager · target + creative + budget + KPI
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Config row */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div>
            <label className="label-mono block mb-1.5">// platform</label>
            <div className="flex flex-wrap gap-1 border border-[#1F1F1F]" data-testid="ads-platform-tabs">
              {PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPlatform(p.id)}
                  disabled={loading}
                  data-testid={`ads-platform-${p.id}`}
                  className={`label-mono px-3 py-1.5 flex-1 ${platform === p.id ? "bg-[#FF7A00] text-white" : "text-zinc-500 hover:text-white"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-mono block mb-1.5">// daily budget (idr)</label>
            <input
              type="number"
              value={budget}
              onChange={(e) => setBudget(parseInt(e.target.value) || 0)}
              disabled={loading}
              placeholder="150000"
              data-testid="ads-budget-input"
              className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm focus:border-[#FF7A00] outline-none"
            />
          </div>

          <div>
            <label className="label-mono block mb-1.5">// data range</label>
            <div className="flex items-center gap-1 border border-[#1F1F1F]">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  disabled={loading}
                  data-testid={`ads-days-${d}`}
                  className={`label-mono px-3 py-1.5 flex-1 ${days === d ? "bg-[#FF7A00] text-white" : "text-zinc-500 hover:text-white"}`}
                >
                  {d} hari
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-end">
            <button
              onClick={generate}
              disabled={loading || disabled}
              data-testid="ads-generate-btn"
              className="w-full border border-[#FF7A00] bg-[#FF7A00]/10 hover:bg-[#FF7A00]/20 px-4 py-2 label-mono flex items-center justify-center gap-2 transition disabled:opacity-40"
            >
              <Megaphone size={12} className={loading ? "animate-pulse" : ""} />
              {loading ? "generating..." : "generate brief"}
            </button>
          </div>
        </div>

        <div>
          <label className="label-mono block mb-1.5">// campaign goal (opsional)</label>
          <textarea
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            disabled={loading}
            rows={2}
            placeholder="Contoh: Naikkan penjualan sablon DTF & jersey custom untuk komunitas futsal Jakarta"
            data-testid="ads-goal-input"
            className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm focus:border-[#FF7A00] outline-none resize-none"
          />
        </div>

        {/* Result */}
        {error && (
          <div className="label-mono text-[#FF3B30] py-2" data-testid="ads-error">// {error}</div>
        )}

        {loading && (
          <div className="label-mono text-zinc-500 py-8 flex items-center gap-2">
            <Clock size={14} className="animate-spin text-[#FF7A00]" />
            gpt-5.2 sedang menyusun campaign brief... (~15-25 detik)
          </div>
        )}

        {result && !loading && (
          <div className="border border-[#1F1F1F] bg-[#050505]" data-testid="ads-brief-result">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1F1F1F]">
              <div className="flex items-center gap-3">
                <span className="label-mono text-[#FF7A00]">{result.platform_label}</span>
                {result.budget_idr && (
                  <span className="label-mono text-zinc-500">
                    · budget: Rp {fmtNum(result.budget_idr)}/hari
                  </span>
                )}
                {result.excluded_bot_channels?.length > 0 && (
                  <span className="label-mono text-[#00E676]">
                    · bot filter: {result.excluded_bot_channels.join(", ")}
                  </span>
                )}
              </div>
              <button
                onClick={copyBrief}
                data-testid="ads-copy-btn"
                className="border border-[#1F1F1F] hover:border-[#FF7A00] px-3 py-1.5 label-mono flex items-center gap-2 transition"
              >
                {copied ? <Check size={12} className="text-[#00E676]" /> : <Copy size={12} />}
                {copied ? "copied" : "copy brief"}
              </button>
            </div>
            <div className="p-4 prose prose-invert prose-sm max-w-none font-body text-zinc-300 whitespace-pre-wrap">
              {result.brief}
            </div>
          </div>
        )}

        {!result && !loading && !error && (
          <div className="text-zinc-500 text-sm label-mono flex items-center gap-2 py-4">
            <Megaphone size={14} className="text-[#FF7A00]" />
            pilih platform, tentukan budget dan goal, lalu klik <span className="text-white">generate brief</span>
          </div>
        )}
      </div>
    </section>
  );
};







export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [period, setPeriod] = useState("daily");
  const [traffic, setTraffic] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [ga4, setGa4] = useState(null);
  const [ga4Loading, setGa4Loading] = useState(true);
  const [ga4Error, setGa4Error] = useState(null);
  const [excludeBots, setExcludeBots] = useState(false);

  const loadGa4 = async (opts = {}) => {
    setGa4Loading(true);
    setGa4Error(null);
    const bots = opts.excludeBots !== undefined ? opts.excludeBots : excludeBots;
    try {
      const { data } = await api.get("/dashboard/ga4", {
        params: { days: 30, top_pages: 10, exclude_bots: bots },
      });
      setGa4(data);
    } catch (err) {
      setGa4Error(err?.response?.data?.detail || "GA4 tidak tersedia");
      setGa4(null);
    } finally {
      setGa4Loading(false);
    }
  };

  const handleToggleBots = (val) => {
    setExcludeBots(val); // useEffect on [excludeBots] will refetch
  };

  const load = async () => {
    try {
      const [ov, gr, cm, tf] = await Promise.all([
        api.get("/dashboard/overview"),
        api.get("/dashboard/growth", { params: { days: 30 } }),
        api.get("/dashboard/comparison"),
        api.get("/dashboard/traffic", { params: { period } }),
      ]);
      setData(ov.data);
      setGrowth(gr.data.series || []);
      setComparison(cm.data.comparison || []);
      setTraffic(tf.data.series || []);
    } catch (err) {
      toast.error("Gagal memuat dashboard");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);
  useEffect(() => {
    loadGa4();
    const t = setInterval(() => loadGa4(), 60000); // refresh every 60s with current filter state
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeBots]);

  const seedDemo = async () => {
    try {
      await api.post("/seed/demo");
      toast.success("Data demo berhasil dibuat");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal seed data");
    }
  };

  const generateSummary = async (p) => {
    setLoadingSummary(true);
    setAiSummary("");
    try {
      const { data } = await api.post("/ai/summary", { period: p });
      setAiSummary(data.summary);
    } catch (err) {
      toast.error("Gagal generate ringkasan AI");
    } finally { setLoadingSummary(false); }
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 label-mono">
        <span className="terminal-cursor">memuat command center</span>
      </div>
    );
  }

  const k = data.kpis;
  const noChannels = data.channels.length === 0;

  // Merge traffic series for combined chart
  const mergedTraffic = (() => {
    if (!traffic.length) return [];
    const dates = traffic[0].data.map(d => d.date);
    return dates.map((date, idx) => {
      const row = { date };
      traffic.forEach(s => { row[s.channel] = s.data[idx]?.value || 0; });
      return row;
    });
  })();

  return (
    <div className="p-6 lg:p-8 space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 pb-2 border-b border-[#1F1F1F]">
        <div>
          <div className="label-mono">// mission control · {new Date().toLocaleDateString("id-ID", {weekday:"long", day:"numeric", month:"long", year:"numeric"})}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none mt-2" data-testid="page-title">
            Dashboard <span className="text-[#00F0FF]">Utama</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-2">Selamat datang, <span className="text-white">{user?.name}</span>. Berikut ringkasan performa {data.kpis.active_channels} channel aktif.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/ai" className="border border-[#1F1F1F] hover:border-[#007AFF] px-4 py-2.5 label-mono flex items-center gap-2 transition" data-testid="btn-open-ai">
            <Sparkles size={12} /> tanya ai
          </Link>
          {user?.role === "super_admin" && noChannels && (
            <button onClick={seedDemo} className="bg-[#007AFF] hover:bg-[#0056b3] px-4 py-2.5 label-mono transition" data-testid="btn-seed-demo">
              + generate demo data
            </button>
          )}
        </div>
      </div>

      {/* GA4 Live Section (real-time, independent of channels) */}
      <Ga4LiveSection
        ga4={ga4}
        loading={ga4Loading}
        error={ga4Error}
        onRefresh={() => loadGa4()}
        excludeBots={excludeBots}
        onToggleBots={handleToggleBots}
      />

      {/* AI Content Recommendation (based on real GA4 data) */}
      <AiContentRecommendation disabled={!ga4} />

      {/* Peak Hours Breakdown (per device + per city) */}
      <PeakHoursBreakdown disabled={!ga4} />

      {/* Ads Campaign Brief Generator */}
      <AdsCampaignBrief disabled={!ga4} />

      {noChannels ? (
        <div className="border border-dashed border-[#1F1F1F] p-16 text-center">
          <Radio size={40} className="mx-auto text-zinc-700 mb-4" />
          <h3 className="font-display font-bold text-2xl mb-2">Belum ada channel aktif</h3>
          <p className="text-zinc-500 text-sm mb-6">Mulai dengan menambahkan channel atau generate data demo untuk melihat command center beraksi.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/channels" className="border border-[#1F1F1F] hover:border-[#007AFF] px-5 py-2.5 label-mono">+ tambah channel</Link>
            {user?.role === "super_admin" && (
              <button onClick={seedDemo} className="bg-[#007AFF] hover:bg-[#0056b3] px-5 py-2.5 label-mono" data-testid="btn-seed-demo-empty">
                generate demo data
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <KPI testid="kpi-visitors" label="pengunjung web" value={fmtNum(k.total_visitors)} icon={Eye} delta={5.2} />
            <KPI testid="kpi-followers" label="total follower" value={fmtNum(k.total_followers)} icon={Users2} delta={3.8} />
            <KPI testid="kpi-subscribers" label="subscriber yt" value={fmtNum(k.total_subscribers)} icon={Activity} delta={2.1} />
            <KPI testid="kpi-orders" label="total order" value={fmtNum(k.total_orders)} icon={ShoppingBag} delta={7.4} />
            <KPI testid="kpi-revenue" label="pendapatan" value={fmtIDR(k.total_revenue)} icon={Wallet} delta={9.1} />
            <KPI testid="kpi-engagement" label="engagement rate" value={`${k.avg_engagement}%`} icon={TrendingUp} delta={-1.2} />
          </div>

          {/* Traffic Chart + Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Section
                title="Traffic Website"
                subtitle={`periode: ${period}`}
                right={
                  <div className="flex items-center gap-1 border border-[#1F1F1F]" data-testid="traffic-period-tabs">
                    {["daily", "weekly", "monthly"].map(p => (
                      <button key={p} onClick={() => setPeriod(p)}
                        data-testid={`traffic-period-${p}`}
                        className={`label-mono px-3 py-1.5 ${period === p ? "bg-[#007AFF] text-white" : "text-zinc-500 hover:text-white"}`}>
                        {p === "daily" ? "harian" : p === "weekly" ? "mingguan" : "bulanan"}
                      </button>
                    ))}
                  </div>
                }
              >
                {mergedTraffic.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={mergedTraffic}>
                      <defs>
                        {traffic.map((s, i) => (
                          <linearGradient key={s.type} id={`g-${s.type}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={CHART_COLORS[i]} stopOpacity={0.4}/>
                            <stop offset="100%" stopColor={CHART_COLORS[i]} stopOpacity={0}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="2 4" stroke="#1F1F1F" />
                      <XAxis dataKey="date" stroke="#71717A" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                      <YAxis stroke="#71717A" fontSize={10} tickFormatter={(v) => fmtNum(v)} />
                      <Tooltip contentStyle={{ background: "#050505", border: "1px solid #1F1F1F", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono", color: "#A1A1AA" }} />
                      {traffic.map((s, i) => (
                        <Area key={s.type} type="monotone" dataKey={s.channel} stroke={CHART_COLORS[i]} strokeWidth={2} fill={`url(#g-${s.type})`} />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-zinc-500 text-sm py-16 text-center label-mono">tidak ada channel web/ga4 aktif</div>
                )}
              </Section>
            </div>

            <Section title="Perbandingan Channel" subtitle="skor performa relatif">
              <div className="space-y-3">
                {comparison.slice(0, 8).map((c, i) => (
                  <div key={c.type} className="flex items-center gap-3" data-testid={`compare-row-${c.type}`}>
                    <span className="font-mono text-xs w-6 text-zinc-600">{String(i+1).padStart(2,"0")}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm truncate">{c.channel}</span>
                        <span className="font-mono text-xs text-zinc-400">{c.score}</span>
                      </div>
                      <div className="h-1 bg-[#1F1F1F] mt-1 relative">
                        <div className="h-full bg-gradient-to-r from-[#007AFF] to-[#00F0FF]"
                             style={{ width: `${Math.min(100, (c.score / (comparison[0]?.score || 1)) * 100)}%` }} />
                      </div>
                    </div>
                    <span className={`label-mono w-14 text-right ${c.growth_pct >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                      {c.growth_pct >= 0 ? "+" : ""}{c.growth_pct}%
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {/* Growth chart per channel */}
          <Section
            title="Pertumbuhan Semua Channel (30 Hari)"
            subtitle="trend performa"
            right={<span className="label-mono">{growth.length} channels</span>}
          >
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={(() => {
                if (!growth.length) return [];
                const dates = growth[0].data.map(d => d.date);
                return dates.map((date, idx) => {
                  const row = { date };
                  growth.forEach(s => { row[s.channel] = s.data[idx]?.value || 0; });
                  return row;
                });
              })()}>
                <CartesianGrid strokeDasharray="2 4" stroke="#1F1F1F" />
                <XAxis dataKey="date" stroke="#71717A" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                <YAxis stroke="#71717A" fontSize={10} tickFormatter={fmtNum} />
                <Tooltip contentStyle={{ background: "#050505", border: "1px solid #1F1F1F", fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono", color: "#A1A1AA" }} />
                {growth.map((s, i) => (
                  <Line key={s.type} type="monotone" dataKey={s.channel} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={1.5} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Section>

          {/* AI Summary card */}
          <Section
            title="AI Auto Summary"
            subtitle="ringkasan otomatis berbasis data terkini"
            right={
              <div className="flex items-center gap-1 border border-[#1F1F1F]">
                {["daily", "weekly", "monthly"].map(p => (
                  <button key={p} onClick={() => generateSummary(p)} disabled={loadingSummary}
                    data-testid={`ai-summary-${p}`}
                    className="label-mono px-3 py-1.5 text-zinc-400 hover:text-white hover:bg-[#0F0F0F] disabled:opacity-40">
                    {p === "daily" ? "harian" : p === "weekly" ? "mingguan" : "bulanan"}
                  </button>
                ))}
              </div>
            }
          >
            {aiSummary ? (
              <div className="prose prose-invert prose-sm max-w-none font-body text-zinc-300 whitespace-pre-wrap" data-testid="ai-summary-content">
                {aiSummary}
              </div>
            ) : (
              <div className="text-zinc-500 text-sm label-mono flex items-center gap-2 py-6">
                {loadingSummary ? (
                  <><Clock size={14} className="animate-spin" /> generating summary...</>
                ) : (
                  <><Sparkles size={14} className="text-[#00F0FF]" /> pilih periode di atas untuk generate ringkasan ai</>
                )}
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
